import React, { useEffect, useState } from 'react'
import { Table, Button, Spinner, Row, Col, Form, Card, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchHodublastReportData, downloadHodublastReportCSV } from '../../../store/slices/hodublast';
import { DateTime } from "luxon";
import Layout from "../../../layouts/default";
import { initializeExportDispatcher } from '../../../store/slices/exportFile/exportFile';
import { hasPermission } from '../../../services/utils';
import { PERMISSIONS } from '../../../contants/permission';

function HodublastUploadReportView() {
    const dispatch = useDispatch();
    const { campaignUuid } = useParams();
    const { hodublastUploadReport } = useSelector(store => store.hodublastReducer);
    const [localFilters, setLocalFilters] = useState({
        jobId: '',
        jobName: '',
        accountName: ''
    });
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [email, setEmail] = useState("");
    const { data, loading, error } = hodublastUploadReport;
    const report = data?.[0];
    const [isSending, setIsSending] = useState(false);
    const [exportLoading, setExportLoading] = useState({});

    useEffect(() => {
        if (campaignUuid) {
            dispatch(fetchHodublastReportData({
                filters: { campaignUuid },
                limit: 1
            }));
        }
    }, [campaignUuid, dispatch]);

    const accountOptions = [
        { value: 'jaipur', label: 'Jaipur' },
        { value: 'pink city', label: 'Pink City' },
    ];

    const handleFilterChange = (field, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = () => {
        toast.info("Filters applied");
    };

    const handleResetFilters = () => {
        setLocalFilters({
            jobId: '',
            jobName: '',
            accountName: ''
        });
        toast.info("Filters reset");
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return "NA";
        const dt = DateTime.fromISO(dateTimeString);
        return dt.isValid ? dt.toFormat("yyyy-MM-dd HH:mm:ss") : "Invalid Date";
    };

    // Get all jobs to display (combining successful and failed jobs)
    const getAllJobsToDisplay = () => {
        if (!report) return [];

        const successfulJobs = (report.jobs || []).map(job => ({
            ...job,
            status: job.status || 'success',
            type: 'success'
        }));

        // Parse failed jobs from JSON strings if needed
        const failedJobs = (report.failedJobs || []).map(failedJob => {
            try {
                // If it's a stringified JSON, parse it
                if (typeof failedJob === 'string') {
                    const parsed = JSON.parse(failedJob);
                    return {
                        jobId: parsed.jobId,
                        jobName: parsed.jobName || 'Unknown Job',
                        hodublastAccount: parsed.accountName,
                        contactsUploaded: parsed.uploadedCount || 0,
                        contactsFailed: parsed.failedCount || parsed.contactsCount || 0,
                        error: parsed.error,
                        status: 'failed',
                        type: 'failed'
                    };
                }
                return {
                    ...failedJob,
                    status: 'failed',
                    type: 'failed'
                };
            } catch {
                return {
                    jobId: 'Unknown',
                    jobName: 'Unknown Job',
                    hodublastAccount: 'Unknown',
                    contactsUploaded: 0,
                    contactsFailed: 0,
                    error: failedJob,
                    status: 'failed',
                    type: 'failed'
                };
            }
        });

        return [...successfulJobs, ...failedJobs];
    };

    // Filter jobs based on local filters
    const allJobs = getAllJobsToDisplay();
    const filteredJobs = allJobs.filter(job => {
        const matchesJobId = !localFilters.jobId ||
            job.jobId?.toString().includes(localFilters.jobId);

        const matchesJobName = !localFilters.jobName ||
            job.jobName?.toLowerCase().includes(localFilters.jobName.toLowerCase());

        const matchesAccount = !localFilters.accountName ||
            job.hodublastAccount?.toLowerCase().includes(localFilters.accountName.value?.toLowerCase());

        return matchesJobId && matchesJobName && matchesAccount;
    });

    // Get job status display info
    const getJobStatusDisplay = (job) => {
        const status = job.status?.toLowerCase();

        switch (status) {
            case 'success':
                return {
                    status: 'Success',
                    color: '#28a745',
                    badgeClass: 'success'
                };
            case 'partial':
                return {
                    status: 'Partial Success',
                    color: '#ffc107',
                    badgeClass: 'warning'
                };
            case 'failed':
                return {
                    status: 'Failed',
                    color: '#dc3545',
                    badgeClass: 'danger'
                };
            default:
                return {
                    status: 'Unknown',
                    color: '#6c757d',
                    badgeClass: 'secondary'
                };
        }
    };

    // Get overall campaign status display
    const getCampaignStatusDisplay = () => {
        const status = report.status?.toLowerCase();

        console.log("lksdfkadss :", status)

        switch (status) {
            case 'success':
                return {
                    status: 'Success',
                    color: '#28a745',
                    description: 'All jobs completed successfully'
                };
            case 'partial':
                return {
                    status: 'Partial Success',
                    color: '#ffc107',
                    description: 'Some jobs completed with failures'
                };
            case 'fail':
                return {
                    status: 'Fail',
                    color: '#dc3545',
                    description: 'All jobs failed'
                };
            default:
                return {
                    status: report.status || 'Unknown',
                    color: '#6c757d',
                    description: 'Status unknown'
                };
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2">Loading report details...</div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="text-center py-5 text-danger">
                    <h4>Error loading report</h4>
                    <p>{error}</p>
                </div>
            </Layout>
        );
    }

    if (!report) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <h4>Report not found</h4>
                </div>
            </Layout>
        );
    }

    const handleBack = () => {
        window.history.back();
    }

    // Function to open modal
    // const handleDownloadCSV = () => {
    //     setShowEmailModal(true);
    // };

    const handleCloseModal = () => {
        setShowEmailModal(false);
        setEmail("");
    };

    const handleSendCSV = async () => {
        if (!email) {
            toast.error("Please enter your email address");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setIsSending(true);

        try {
            const response = await dispatch(
                downloadHodublastReportCSV({
                    reportId: report._id,
                    email: email,
                })
            ).unwrap();

            toast.success(response || "CSV download link has been sent to your email!");
            handleCloseModal();
        } catch (error) {
            console.error("Error in handleSendCSV:", error);
            toast.error(error || "Failed to send CSV. Please try again.");
        } finally {
            setIsSending(false);
        }
    };


    const handleExportFailedContacts = async (job) => {
        try {
            const key = `${job.jobId}-${job.hodublastAccount}`;

            // Start loading for this specific job
            setExportLoading(prev => ({ ...prev, [key]: true }));

            const { jobId, hodublastAccount } = job;

            if (!jobId || !hodublastAccount) {
                toast.error("hodublast jobId or accountName is missing.");
                return;
            }

            const user = JSON.parse(localStorage.getItem("user"));
            if (!user) {
                toast.error("user not found. please re-login");
                return;
            }

            const campaignId = report?.campaignId;
            const campaignUuid = report?.campaignUuid;

            const payload = {
                jobId,
                hodublastAccount,
                user: user?.data || null,
                exportType: "IVR_FAILED_EXPORT",
                campaignId,
                campaignUuid,
            };

            const response = await dispatch(initializeExportDispatcher(payload)).unwrap();

            toast.success(response);
        } catch (err) {
            console.log("Error in handleExportFailedContacts : ", err);
            toast.error(err || "Something went wrong. please try again later");
        } finally {
            // Stop loading for this job
            const key = `${job.jobId}-${job.hodublastAccount}`;
            setExportLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    const campaignStatus = getCampaignStatusDisplay();

    return (
        <Layout>
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button variant="secondary" onClick={handleBack}>
                        ← Back
                    </Button>
                    {/* <Button variant="primary" onClick={handleDownloadCSV}>
                        Download Uploaded CSV
                    </Button> */}
                </div>

                {/* Email Input Modal */}
                <Modal show={showEmailModal} onHide={handleCloseModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Send CSV Report</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group controlId="emailInput">
                                <Form.Label>Enter your Email ID</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal} disabled={isSending}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSendCSV} disabled={isSending}>
                            {isSending ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{" "}
                                    Sending...
                                </>
                            ) : (
                                "Submit"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Campaign Summary Card */}
                <Card className="mb-4 shadow border-0">
                    <Card.Header
                        className="d-flex justify-content-between align-items-center py-3 px-4"
                        style={{
                            background: "linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)",
                            borderBottom: "1px solid #dee2e6",
                        }}
                    >
                        <h5 className="mb-0 fw-bold text-dark">Campaign Summary</h5>
                        <span
                            className="badge rounded-pill px-3 py-2"
                            style={{
                                fontSize: "0.85rem",
                                backgroundColor: campaignStatus.color,
                                color: campaignStatus.color === "#ffc107" ? "#212529" : "#fff",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                            }}
                        >
                            {campaignStatus.status}
                        </span>
                    </Card.Header>

                    <Card.Body className="pt-4 pb-3 px-4">
                        {/* Section 1 */}
                        <Row className="mb-3 gy-3">
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Campaign ID</div>
                                    <div className="fw-semibold fs-6 text-dark">{report.campaignId || "NA"}</div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Campaign Name</div>
                                    <div className="fw-semibold fs-6 text-dark text-truncate">
                                        {report.campaignName || "NA"}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Trigger Date</div>
                                    <div className="fw-semibold fs-6 text-dark">
                                        {formatDateTime(report.triggerDateTime)}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Campaign Uuid</div>
                                    <div className="fw-semibold fs-6 text-dark">
                                        {report?.campaignUuid || "NA"}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Section 2 */}
                        <Row className="mb-3 gy-3">
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-white border">
                                    <div className="text-muted small">Status</div>
                                    <div
                                        className="fw-bold fs-6"
                                        style={{ color: campaignStatus.color }}
                                    >
                                        {campaignStatus.status}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-white border">
                                    <div className="text-muted small">Total Jobs</div>
                                    <div className="fw-semibold fs-6 text-dark">{allJobs.length || 0}</div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-white border">
                                    <div className="text-muted small">Successful Jobs</div>
                                    <div className="fw-semibold fs-6 text-success">
                                        {allJobs.filter((job) => job.status === "success").length || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-white border">
                                    <div className="text-muted small">Failed Jobs</div>
                                    <div className="fw-semibold fs-6 text-danger">
                                        {allJobs.filter((job) => job.status === "failed").length || 0}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Section 3 */}
                        <Row className="gy-3">
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-white border">
                                    <div className="text-muted small">Total Contacts In File</div>
                                    <div className="fw-semibold fs-6 text-success">
                                        {report?.totalValidContactsInCsvFile || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Total Contacts Excluded</div>
                                    <div className="fw-semibold fs-6 text-warning">
                                        {report?.totalContactExcluded || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Total Contacts Uploaded</div>
                                    <div className="fw-semibold fs-6 text-dark">
                                        {report.totalContactsUploaded || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} sm={6}>
                                <div className="p-3 rounded bg-light border">
                                    <div className="text-muted small">Total Contacts Failed</div>
                                    <div className="fw-semibold fs-6 text-danger">
                                        {report.totalContactsFailed || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div
                                    className="p-3 rounded border"
                                    style={{
                                        background: "#fefefe",
                                        borderLeft: `4px solid ${campaignStatus.color}`,
                                    }}
                                >
                                    <small className="text-muted">{campaignStatus.description}</small>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Filter Section */}
                {/* <div className="bg-light p-3 mb-4 rounded">
                    <h5 className="mb-3">Job Filters</h5>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Job ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Job ID"
                                    value={localFilters.jobId}
                                    onChange={(e) => handleFilterChange('jobId', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Job Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Job Name"
                                    value={localFilters.jobName}
                                    onChange={(e) => handleFilterChange('jobName', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Account Name</Form.Label>
                                <Select
                                    options={accountOptions}
                                    value={localFilters.accountName}
                                    onChange={(selected) => handleFilterChange('accountName', selected)}
                                    isClearable
                                    placeholder="Select Account"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleResetFilters}
                                >
                                    Reset Filters
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div> */}

                {/* Jobs Table */}
                <h5 className="mb-3">Job Details ({filteredJobs.length} jobs)</h5>
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>SR.No.</th>
                            <th>Job ID</th>
                            <th>Job Name</th>
                            <th>Account</th>
                            <th>Contacts Uploaded</th>
                            <th>Contacts Failed</th>
                            <th>Total Contacts</th>
                            <th>Job Status</th>
                            <th>Error Details</th>
                            {
                                hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DOWNLOAD) && <th>Export</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {filteredJobs.length > 0 ? (
                            filteredJobs.map((job, index) => {
                                const jobStatus = getJobStatusDisplay(job);
                                const totalContacts = (job.contactsUploaded || 0) + (job.contactsFailed || 0);

                                return (
                                    <tr key={`${job.jobId}-${job.hodublastAccount}-${index}`}>
                                        <td>{index + 1}</td>
                                        <td>{job.jobId || "NA"}</td>
                                        <td>{job.jobName || "NA"}</td>
                                        <td>{job.hodublastAccount || "NA"}</td>
                                        <td className="text-success">
                                            <strong>{job.contactsUploaded || 0}</strong>
                                        </td>
                                        <td className="text-danger">
                                            <strong>{job.contactsFailed || 0}</strong>
                                        </td>
                                        <td>
                                            <strong>{totalContacts}</strong>
                                        </td>
                                        <td>
                                            <span
                                                className={`badge badge-${jobStatus.badgeClass}`}
                                                style={{
                                                    backgroundColor: jobStatus.color,
                                                    color: jobStatus.color === '#ffc107' ? '#212529' : 'white'
                                                }}
                                            >
                                                {jobStatus.status}
                                            </span>
                                        </td>
                                        <td>
                                            {job.error ? (
                                                <small className="text-danger">
                                                    {typeof job.error === 'string' ? job.error : JSON.stringify(job.error)}
                                                </small>
                                            ) : (
                                                <span className="text-muted">No errors</span>
                                            )}
                                        </td>
                                        {
                                            hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DOWNLOAD) && <td>
                                                <Button
                                                    onClick={() => handleExportFailedContacts(job)}
                                                    disabled={job?.contactsFailed <= 0 || exportLoading[`${job.jobId}-${job.hodublastAccount}`]}
                                                    variant="outline-danger"
                                                    style={{
                                                        fontWeight: "600",
                                                        borderRadius: "6px",
                                                        padding: "6px 14px",
                                                        cursor: "pointer",
                                                        opacity: job?.contactsFailed <= 0 ? 0.5 : 1,
                                                    }}
                                                >
                                                    {exportLoading[`${job.jobId}-${job.hodublastAccount}`] ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                                style={{ marginRight: "6px" }}
                                                            />
                                                            Exporting...
                                                        </>
                                                    ) : (
                                                        "Failed Export"
                                                    )}
                                                </Button>
                                            </td>
                                        }

                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center py-4">
                                    No jobs found matching the filters
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Status Legend */}
                <div className="mt-4 p-3 bg-light rounded">
                    <h6>Status Legend:</h6>
                    <div className="d-flex flex-wrap gap-3">
                        <div className="d-flex align-items-center">
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#28a745',
                                    borderRadius: '50%',
                                    marginRight: '5px'
                                }}
                            ></div>
                            <small>Success - All contacts uploaded successfully</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#ffc107',
                                    borderRadius: '50%',
                                    marginRight: '5px'
                                }}
                            ></div>
                            <small>Partial Success - Some contacts failed to upload</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#dc3545',
                                    borderRadius: '50%',
                                    marginRight: '5px'
                                }}
                            ></div>
                            <small>Failed - All contacts failed to upload</small>
                        </div>
                    </div>
                </div>

                {/* Show message if no jobs in report */}
                {allJobs.length === 0 && (
                    <div className="text-center py-4">
                        <h5>No jobs found in this report</h5>
                    </div>
                )}
            </div>
        </Layout>
    )
}

export default HodublastUploadReportView;